import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Flame, Speaker, Star, XCircle } from 'lucide-react';
import {
  abandonListenPickRun,
  answerListenPick,
  completeListenPickRun,
  createListenPickRun,
  getListenPickAudio,
  resumeListenPickRun,
  type ArcadeAnswerResult,
  type ArcadeCompleteResult,
  type ListenPickQuestion,
} from '../api/learning';
import { ArcadeSummaryView } from '../components/ui';
import { usePreloadedAudio } from '../hooks/usePreloadedAudio';
import './ListenPickGame.css';

function isListenPickQuestion(question: unknown): question is ListenPickQuestion {
  if (typeof question !== 'object' || question === null) return false;
  const value = question as Record<string, unknown>;
  return (
    value['type'] === 'listen-pick' &&
    typeof value['id'] === 'string' &&
    typeof value['prompt'] === 'string' &&
    typeof value['audioRef'] === 'string' &&
    typeof value['visibleText'] === 'string' &&
    Array.isArray(value['options']) &&
    value['options'].every(option => typeof option === 'string')
  );
}

type ConfirmedScore = Pick<ArcadeAnswerResult, 'score' | 'combo' | 'bestCombo'>;
type PendingAnswer = { questionId: string; answer: string };

export function ListenPickGame() {
  const [runId, setRunId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<ListenPickQuestion[]>([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [score, setScore] = useState<ConfirmedScore>({ score: 0, combo: 0, bestCombo: 0 });
  const [feedback, setFeedback] = useState<ArcadeAnswerResult | null>(null);
  const [summary, setSummary] = useState<ArcadeCompleteResult | null>(null);
  const [pendingAnswer, setPendingAnswer] = useState<PendingAnswer | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startFailed, setStartFailed] = useState(false);

  async function start() {
    setLoading(true);
    setError(null);
    setStartFailed(false);
    setRunId(null);
    setQuestions([]);
    setQuestionIndex(0);
    setScore({ score: 0, combo: 0, bestCombo: 0 });
    setSummary(null);
    setFeedback(null);
    setPendingAnswer(null);
    try {
      const run = await createListenPickRun();
      const listenQuestions = run.questions.filter(isListenPickQuestion);
      if (listenQuestions.length !== run.questions.length) {
        throw new Error('Unexpected question payload');
      }
      setRunId(run.runId);
      setQuestions(listenQuestions);
      setQuestionIndex(run.currentQuestionIndex);
      setScore({ score: run.score, combo: run.combo, bestCombo: run.bestCombo });
    } catch {
      setStartFailed(true);
      setError('Listen & Pick is unavailable right now. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void start();
  }, []);

  const question = questions[questionIndex];
  const audio = usePreloadedAudio(question?.audioRef ?? null, getListenPickAudio);
  const audioError = 'Audio is unavailable. Use the visible phrase below to keep playing.';

  function playAudio() {
    setError(null);
    void audio.play().catch(() => setError(audioError));
  }

  async function submit(answer: PendingAnswer) {
    if (!runId) return;
    setSubmitting(true);
    setError(null);
    setPendingAnswer(answer);
    try {
      const result = await answerListenPick(runId, answer.questionId, answer.answer);
      setScore({ score: result.score, combo: result.combo, bestCombo: result.bestCombo });
      setFeedback(result);
      setPendingAnswer(null);
      if (result.complete) {
        try {
          setSummary(await completeListenPickRun(runId));
        } catch {
          setError(
            'Your answer was confirmed, but the round summary is unavailable. Retry the summary.'
          );
        }
      }
    } catch {
      setError('Your answer was not confirmed. Your score and combo are unchanged.');
    } finally {
      setSubmitting(false);
    }
  }

  async function retrySummary() {
    if (!runId) return;
    setSubmitting(true);
    setError(null);
    try {
      setSummary(await completeListenPickRun(runId));
    } catch {
      setError('The round summary is still unavailable. Your confirmed score is unchanged.');
    } finally {
      setSubmitting(false);
    }
  }

  async function resume() {
    if (!runId) return;
    setLoading(true);
    setError(null);
    try {
      const run = await resumeListenPickRun(runId);
      const listenQuestions = run.questions.filter(isListenPickQuestion);
      if (listenQuestions.length !== run.questions.length) {
        throw new Error('Unexpected question payload');
      }
      setQuestions(listenQuestions);
      setQuestionIndex(run.currentQuestionIndex);
      setScore({ score: run.score, combo: run.combo, bestCombo: run.bestCombo });
      setFeedback(null);
      setPendingAnswer(null);
      if (run.status !== 'in_progress') {
        setError('This round has ended. Start a new round to keep practicing.');
      }
    } catch {
      setError('We could not restore the round. Your last confirmed score is still shown.');
    } finally {
      setLoading(false);
    }
  }

  async function abandon() {
    if (!runId) return;
    setSubmitting(true);
    setError(null);
    try {
      await abandonListenPickRun(runId);
      setRunId(null);
      setQuestions([]);
      setFeedback(null);
      setPendingAnswer(null);
      setScore({ score: 0, combo: 0, bestCombo: 0 });
      setError('Round ended. An unverified score was not saved.');
    } catch {
      setError('We could not end the round. Resume it before starting another.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading && !runId)
    return (
      <p role="status" data-testid="listen-pick-loading">
        Preparing Listen & Pick…
      </p>
    );

  if (summary) {
    return (
      <ArcadeSummaryView
        heading="Round complete"
        verifiedScore={summary.verifiedScore}
        accuracy={summary.accuracy}
        correctCount={summary.correctCount}
        answeredCount={summary.answeredCount}
        isNewPersonalBest={summary.isNewPersonalBest}
        additionalStats={[{ label: 'Best combo', value: `×${summary.bestCombo}` }]}
        playAgainLabel="Play another round"
        playAgainTestId="listen-pick-play-again"
        onPlayAgain={start}
        testId="listen-pick-summary"
      />
    );
  }

  return (
    <section aria-labelledby="listen-pick-heading" data-testid="listen-pick-game">
      <div className="learning-section-heading">
        <div>
          <h2 id="listen-pick-heading">Listen &amp; Pick</h2>
          <p>Hear your phrase, then choose its matching translation.</p>
        </div>
        <span className="learning-count">
          {questions.length === 0 ? 0 : Math.min(questionIndex + 1, questions.length)} /{' '}
          {questions.length}
        </span>
      </div>

      <div className="listen-scoreboard" aria-label="Server-confirmed game progress">
        <span>
          <Star aria-hidden="true" /> Score <strong>{score.score}</strong>
        </span>
        <span>
          <Flame aria-hidden="true" /> Combo <strong>{score.combo}</strong>
        </span>
        <span>Best combo {score.bestCombo}</span>
      </div>

      {(error || audio.loadFailed) && (
        <div
          className="learning-notice learning-notice--error"
          role="alert"
          data-testid="listen-pick-error"
        >
          <AlertCircle aria-hidden="true" /> <span>{error ?? audioError}</span>
        </div>
      )}

      {!question ? (
        <div className="learning-empty" data-testid="listen-pick-empty">
          <Speaker aria-hidden="true" />
          <h3>{startFailed ? 'Listening round unavailable' : 'No listening questions yet'}</h3>
          <p>
            {startFailed
              ? 'The round could not be started. Try again in a moment.'
              : 'Save translated vocabulary to create a round.'}
          </p>
          <button className="listen-primary" onClick={start} data-testid="listen-pick-retry-start">
            Try again
          </button>
        </div>
      ) : (
        <div className="listen-card" data-testid="listen-pick-card">
          <p className="listen-prompt">{question.prompt}</p>
          <button
            className="listen-audio-btn"
            onClick={playAudio}
            disabled={!audio.ready || audio.playing || submitting}
            aria-label={
              audio.playing ? 'Playing listening phrase' : 'Play or replay listening phrase'
            }
            data-testid="listen-pick-audio-btn"
          >
            <Speaker aria-hidden="true" />{' '}
            {!audio.ready ? 'Loading audio…' : audio.playing ? 'Playing…' : 'Play / replay phrase'}
          </button>
          <p className="listen-visible-label">Visible phrase</p>
          <p className="listen-visible-text" data-testid="listen-pick-visible-text">
            {question.visibleText}
          </p>

          <div
            className="listen-options"
            aria-label="Translation choices"
            data-testid="listen-pick-options"
          >
            {question.options.map(option => (
              <button
                key={option}
                disabled={submitting || feedback !== null}
                onClick={() => submit({ questionId: question.id, answer: option })}
                data-testid={`listen-option-${option}`}
              >
                {option}
              </button>
            ))}
          </div>

          {feedback && (
            <div
              className={`listen-feedback listen-feedback--${feedback.correct ? 'correct' : 'incorrect'}`}
              role="status"
              aria-live="polite"
              data-testid="listen-pick-feedback"
            >
              {feedback.correct ? (
                <CheckCircle2 aria-hidden="true" />
              ) : (
                <XCircle aria-hidden="true" />
              )}
              <span>
                <strong>{feedback.correct ? 'Correct!' : 'Not this time.'}</strong>{' '}
                {feedback.correct
                  ? `+${feedback.scoreDelta} points.`
                  : `Answer: ${feedback.correctAnswer}`}
              </span>
              {!feedback.complete && (
                <button
                  onClick={() => {
                    setQuestionIndex(feedback.answeredCount);
                    setFeedback(null);
                  }}
                  data-testid="listen-pick-next"
                >
                  Next phrase
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {runId && !summary && (
        <div className="listen-recovery" data-testid="listen-pick-recovery">
          {feedback?.complete && (
            <button
              disabled={submitting}
              onClick={retrySummary}
              data-testid="listen-pick-retry-summary"
            >
              Retry round summary
            </button>
          )}
          {pendingAnswer && (
            <button
              disabled={submitting}
              onClick={() => submit(pendingAnswer)}
              data-testid="listen-pick-retry"
            >
              Retry answer
            </button>
          )}
          <button
            disabled={submitting || loading}
            onClick={resume}
            data-testid="listen-pick-resume"
          >
            Resume confirmed state
          </button>
          <button
            className="listen-abandon"
            disabled={submitting}
            onClick={abandon}
            data-testid="listen-pick-abandon"
          >
            End round
          </button>
        </div>
      )}
    </section>
  );
}
